<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import Select from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: 'arrow-right',
  },
  inputId: {
    type: String,
    default: '',
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

// List of icons available for selection, organized by category
const availableIcons = computed(() => [
  // Actions and Status
  { icon: 'arrow-right', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.ADVANCE') },
  { icon: 'checkmark', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.COMPLETED') },
  {
    icon: 'checkmark-circle',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.APPROVED'),
  },
  { icon: 'add-circle', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.NEW') },
  {
    icon: 'star-emphasis',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.HIGHLIGHT'),
  },
  { icon: 'heart', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.FAVORITE') },
  {
    icon: 'heart-handshake',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.AGREEMENT'),
  },

  // People and Communication
  {
    icon: 'person-add',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.PROSPECTING'),
  },
  { icon: 'people', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.TEAM') },
  { icon: 'chat', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.CONVERSATION') },
  { icon: 'mail-inbox', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.INBOX') },
  { icon: 'send', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.SEND') },
  {
    icon: 'megaphone',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.ANNOUNCEMENT'),
  },

  // Documents and Organization
  { icon: 'document', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.DOCUMENT') },
  { icon: 'book', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.MANUAL') },
  {
    icon: 'book-copy',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.DOCUMENTATION'),
  },
  { icon: 'folder', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.FOLDER') },
  { icon: 'filter', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.FILTER') },
  { icon: 'list', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.LIST') },

  // Business and Finance
  { icon: 'briefcase', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.BUSINESS') },
  {
    icon: 'credit-card-person',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.PAYMENT'),
  },
  { icon: 'tag', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.TAG') },

  // Technology and Tools
  { icon: 'settings', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.SETTINGS') },
  { icon: 'code', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.DEVELOPMENT') },
  { icon: 'flash-on', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.TEST') },
  { icon: 'key', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.ACCESS') },
  { icon: 'globe', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.WEB') },

  // Time and Calendar
  { icon: 'calendar', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.SCHEDULE') },
  { icon: 'calendar-clock', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.TIME') },

  // Location and Navigation
  { icon: 'location', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.LOCATION') },
  { icon: 'home', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.HOME') },
  { icon: 'search', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.SEARCH') },

  // Visual and Media
  { icon: 'eye-show', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.VIEW') },
  { icon: 'image', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.IMAGE') },
  { icon: 'emoji', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.EMOJI') },

  // Indicators
  { icon: 'flame', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.URGENT') },
  {
    icon: 'arrow-trending-lines',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.TREND'),
  },
  { icon: 'warning', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.WARNING') },
  { icon: 'info', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.INFO') },
]);

const selectedIcon = computed(() => {
  return (
    availableIcons.value.find(i => i.icon === props.modelValue) || {
      icon: 'arrow-right',
      label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.DEFAULT'),
    }
  );
});

const iconOptions = computed(() =>
  availableIcons.value.map(item => ({ value: item.icon, label: item.label }))
);

const selectedValue = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});
</script>

<template>
  <div class="flex w-full items-center gap-2" @click.stop>
    <div
      aria-hidden="true"
      class="flex size-11 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2"
    >
      <FluentIcon
        :icon="selectedIcon.icon"
        size="18"
        :icons="dashboardIcons"
        class="text-n-slate-11"
      />
    </div>
    <Select
      v-model="selectedValue"
      :input-id="inputId"
      :aria-label="ariaLabel"
      :name="name"
      :options="iconOptions"
      class="min-w-0 flex-1 [&>select]:min-h-11 [&>select]:w-full"
    />
  </div>
</template>
